import React, { Fragment, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { isEmpty, lowerCase } from 'lodash';
import {
  MdAddCircle,
  MdNewReleases,
  MdOutlineRemoveCircle,
  MdSearch,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  CelKey,
  PanelTitle,
  Positions,
} from '@modules/permissionManager/constants';
import {
  Button,
  Checkbox,
  ConditionalRender,
  Form,
  Input,
  PersonAvatar,
  S,
  Table,
  Tooltip,
} from '@modules/shared/components';
import type { FormInstance } from '@shared/components/Form/Form.types';
import { hidePositions, unitTypes } from '@shared/constants';
import { isMobileSel } from '@shared/duck/selectors';
import { intl } from '@shared/localization';

import styles from '../permissionsTab.less';
import {
  PermsConfigurationObject,
  RosterAdultMemberExtended,
  RosterYouthMemberExtended,
  getPermisionPositionByPanelType,
} from '../utils';
import { PermsPanelFilterCard } from './PermsPanelFilter';

const { Item: FormItem } = Form;

interface PermissionsPanelProps {
  searchGlobalTxt?: string;
  globalPositionsFilter?: string[];
  form: FormInstance;
  loading: boolean;
  unitType: string;
  tableData: (RosterAdultMemberExtended | RosterYouthMemberExtended)[];
  featurePermissionPositionsWithId:
    | Record<
        string,
        {
          id: number;
          name: string;
        }
      >
    | undefined;
  panelType: PanelTitle;
  onChangeCheckbox: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClickSelectRecommended: (
    recommendedCells: {
      celKey: string;
      positionId: number;
      personGuid: string;
    }[],
  ) => void;
  onClickRemoveRecommended: (
    recommendedCells: {
      celKey: string;
      positionId: number;
      personGuid: string;
    }[],
  ) => void;
}

export const PermissionsPanel = ({
  searchGlobalTxt = '',
  globalPositionsFilter,
  form,
  loading,
  unitType,
  tableData,
  featurePermissionPositionsWithId,
  panelType,
  onChangeCheckbox,
  onClickSelectRecommended,
  onClickRemoveRecommended,
}: PermissionsPanelProps) => {
  const { getFieldDecorator, setFieldsValue } = form;
  const isMobile = useSelector(isMobileSel);
  const [searchTxt, setSearchTxt] = useState<string>('');
  const [positionsFilter, setPositionsFilter] = useState<string[]>([]);

  const [filteredTableData, setFilteredTableData] = useState<
    (RosterAdultMemberExtended | RosterYouthMemberExtended)[]
  >([]);

  useEffect(() => {
    if (loading || !featurePermissionPositionsWithId) return;

    const assignablePositionsIds = getPermisionPositionByPanelType(
      featurePermissionPositionsWithId,
    )[panelType];
    const originalData = tableData;
    originalData.forEach(user => {
      const userPermsPositions = user.registrationData.filter(reg =>
        assignablePositionsIds.includes(reg.positionId),
      );
      if (userPermsPositions.length) {
        userPermsPositions.forEach(pos => {
          setFieldsValue({ [`${user.personGuid}_${pos.positionId}`]: true });
        });
      }
    });
  }, [
    tableData,
    setFieldsValue,
    panelType,
    featurePermissionPositionsWithId,
    loading,
  ]);

  useEffect(() => {
    setSearchTxt(searchGlobalTxt);
  }, [searchGlobalTxt]);

  useEffect(() => {
    if (globalPositionsFilter) {
      setPositionsFilter(globalPositionsFilter);
    }
  }, [globalPositionsFilter]);

  useEffect(() => {
    let resultFilteredData = tableData;

    if (searchTxt) {
      resultFilteredData = resultFilteredData.filter(
        ({ personFullName, nickName, lastName }) =>
          lowerCase(
            nickName ? `${nickName} ${lastName}` : personFullName,
          ).includes(lowerCase(searchTxt)),
      );
    }

    if (positionsFilter.length) {
      resultFilteredData = resultFilteredData.filter(({ userOrgPositions }) =>
        userOrgPositions.some(({ positionLong }) =>
          positionsFilter.includes(positionLong),
        ),
      );
    }

    setFilteredTableData(resultFilteredData);
  }, [tableData, searchTxt, positionsFilter]);

  const { unitLabel, subUnitLabel } = useMemo(() => {
    if (unitType === unitTypes.TROOP)
      return { unitLabel: 'Troop', subUnitLabel: 'Patrol' };
    if (unitType === unitTypes.PACK)
      return { unitLabel: 'Pack', subUnitLabel: 'Den' };
    return { unitLabel: 'Unit', subUnitLabel: 'Subunit' };
  }, [unitType]);

  const handlePositionFilterChange = (filterValue: string[]) =>
    setPositionsFilter(filterValue);

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChangeCheckbox(e);
  };

  const usersRecommendedCells = useMemo(() => {
    if (!featurePermissionPositionsWithId) return [];

    const assignablePositionsIds = getPermisionPositionByPanelType(
      featurePermissionPositionsWithId,
    )[panelType];
    const celKeysByPositionIds = Object.entries(
      featurePermissionPositionsWithId,
    ).reduce((acc, [celKey, positionObj]) => {
      if (assignablePositionsIds.includes(positionObj.id)) {
        return [...acc, { celKey, positionId: positionObj.id }];
      }
      return acc;
    }, []);
    const recommendedCells: {
      celKey: string;
      positionId: number;
      personGuid: string;
    }[] = [];
    tableData.forEach(user => {
      celKeysByPositionIds.forEach(celKeyObj => {
        const celPerms = user.allPositions.reduce((acc, pos) => {
          const celPerm =
            // @ts-expect-error TODO: PermsConfigurationObject type
            PermsConfigurationObject[pos]?.[celKeyObj.celKey];
          return { ...acc, ...celPerm };
        }, {});
        if (!celPerms.isUnitRequired && celPerms.isRecommended) {
          recommendedCells.push({
            celKey: celKeyObj.celKey,
            positionId: celKeyObj.positionId,
            personGuid: user.personGuid,
          });
        }
      });
    });
    return recommendedCells;
  }, [tableData, panelType, featurePermissionPositionsWithId]);

  const handleSelectRecommended = () => {
    const filtered = usersRecommendedCells.filter(item =>
      filteredTableData.find(data => data.personGuid === item.personGuid),
    );
    const formattedForForm = filtered.reduce(
      (acc, curr) => ({
        ...acc,
        [`${curr.personGuid}_${curr.positionId}`]: true,
      }),
      {},
    );
    setFieldsValue(formattedForForm);
    onClickSelectRecommended(filtered);
  };

  const hanldeRemoveRecommended = () => {
    const filtered = usersRecommendedCells.filter(item =>
      filteredTableData.find(data => data.personGuid === item.personGuid),
    );
    const formattedForForm = filtered.reduce(
      (acc, curr) => ({
        ...acc,
        [`${curr.personGuid}_${curr.positionId}`]: false,
      }),
      {},
    );
    setFieldsValue(formattedForForm);
    onClickRemoveRecommended(filtered);
  };

  const getCell = (
    personGuid: string,
    positions: string[],
    celKey: CelKey,
    isExpired: boolean = false,
  ) => {
    if (isExpired) return null;

    const celPerms = positions.reduce((acc, pos) => {
      const celPerm =
        // @ts-expect-error TODO: PermsConfigurationObject type
        PermsConfigurationObject[pos]?.[celKey];

      return { ...acc, ...celPerm };
    }, {});

    if (!celPerms) {
      return null;
    }

    if (celPerms.isUnitRequired) {
      return (
        <div className={styles.chkbxContainer}>
          <Checkbox disabled checked />
        </div>
      );
    }

    if (celPerms.isRecommended && featurePermissionPositionsWithId) {
      return (
        <div className={styles.chkbxContainer}>
          <div className={styles.recommendedChkbxContainer}>
            <FormItem className={styles.formItem}>
              {getFieldDecorator(
                `${personGuid}_${featurePermissionPositionsWithId[celKey].id}`,
                {
                  valuePropName: 'checked',
                },
              )(
                <Checkbox
                  dataset={`${personGuid}_${featurePermissionPositionsWithId[celKey].id}`}
                  onChange={handleCheck}
                />,
              )}
            </FormItem>
            <MdNewReleases className={styles.icon} />
          </div>
        </div>
      );
    }

    if (
      celPerms.isUnitEnabled &&
      !celPerms.isSubRequired &&
      !celPerms.isSubEnabled &&
      featurePermissionPositionsWithId
    ) {
      return (
        <div className={styles.chkbxContainer}>
          <FormItem className={styles.formItem}>
            {getFieldDecorator(
              `${personGuid}_${featurePermissionPositionsWithId[celKey].id}`,
              {
                valuePropName: 'checked',
              },
            )(
              <Checkbox
                dataset={`${personGuid}_${featurePermissionPositionsWithId[celKey].id}`}
                onChange={handleCheck}
              />,
            )}
          </FormItem>
        </div>
      );
    }

    if (
      (celPerms.isSubRequired || celPerms.isSubEnabled) &&
      featurePermissionPositionsWithId
    ) {
      const subCelKey =
        celKey === CelKey.CALENDAR_CREATE ? CelKey.SUB_CALENDAR_CREATE : celKey;

      return (
        <div
          className={cn(
            styles.chkbxContainer,
            styles.mobileDoubleChkbxContainer,
          )}
        >
          <div>
            <S>{subUnitLabel}</S>
            {celPerms.isSubEnabled ? (
              <FormItem className={styles.formItem}>
                {getFieldDecorator(
                  `${personGuid}_${featurePermissionPositionsWithId[subCelKey].id}`,
                  {
                    valuePropName: 'checked',
                  },
                )(
                  <Checkbox
                    dataset={`${personGuid}_${featurePermissionPositionsWithId[subCelKey].id}`}
                    onChange={handleCheck}
                    disabled={celPerms.isSubRequired}
                  />,
                )}
              </FormItem>
            ) : (
              <Checkbox checked disabled />
            )}
          </div>
          {celPerms.isUnitEnabled && (
            <div className={styles.secondaryChcbx}>
              <S>{unitLabel}</S>
              <FormItem className={styles.formItem}>
                {getFieldDecorator(
                  `${personGuid}_${featurePermissionPositionsWithId[celKey].id}`,
                  {
                    valuePropName: 'checked',
                  },
                )(
                  <Checkbox
                    dataset={`${personGuid}_${featurePermissionPositionsWithId[celKey].id}`}
                    onChange={handleCheck}
                  />,
                )}
              </FormItem>
            </div>
          )}
        </div>
      );
    }

    if (celPerms.cannotAssign) {
      return (
        <div className={styles.chkbxContainer}>
          <S size="5" bold>
            Cannot Assign
          </S>
        </div>
      );
    }

    return null;
  };

  const getProgramSpecificUnitColumns = (
    mobileData?: RosterAdultMemberExtended | RosterYouthMemberExtended,
  ) => {
    const isMobileComponent = isMobile && mobileData;
    if (unitType === unitTypes.PACK) {
      if (isMobileComponent) {
        return [
          <div key="denCreate">
            <S bold size="5">
              <FormattedMessage id="permissionManager.tabTitle.denCreate" />
            </S>
            <div>
              {getCell(
                mobileData.personGuid,
                mobileData.allPositions,
                CelKey.SUBUNIT_CREATE,
                mobileData.isExpired,
              )}
            </div>
          </div>,
          <div key="denEdit">
            <S bold size="5">
              <FormattedMessage id="permissionManager.tabTitle.denEdit" />
            </S>
            <div>
              {getCell(
                mobileData.personGuid,
                mobileData.allPositions,
                CelKey.SUBUNIT_EDIT,
                mobileData.isExpired,
              )}
            </div>
          </div>,
          <div key="denChiefAdd">
            <S bold size="5">
              <FormattedMessage id="permissionManager.tabTitle.denChiefAdd" />
            </S>
            <div>
              {getCell(
                mobileData.personGuid,
                mobileData.allPositions,
                CelKey.SUBUNIT_DENCHIEF_ADD,
                mobileData.isExpired,
              )}
            </div>
          </div>,
        ];
      }

      return [
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.denCreate',
          }),
          dataIndex: 'denCreate',
          key: 'denCreate',
          width: '12%',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.personGuid,
              data.allPositions,
              CelKey.SUBUNIT_CREATE,
              data.isExpired,
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.denEdit',
          }),
          dataIndex: 'denEdit',
          key: 'denEdit',
          width: '12%',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.personGuid,
              data.allPositions,
              CelKey.SUBUNIT_EDIT,
              data.isExpired,
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.denChiefAdd',
          }),
          dataIndex: 'denChiefAdd',
          key: 'denChiefAdd',
          width: '12%',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.personGuid,
              data.allPositions,
              CelKey.SUBUNIT_DENCHIEF_ADD,
              data.isExpired,
            );
          },
        },
      ];
    }

    if (unitType === unitTypes.TROOP) {
      if (isMobileComponent) {
        return [
          <div key="patrolCreate">
            <S bold size="5">
              <FormattedMessage id="permissionManager.tabTitle.patrolCreate" />
            </S>
            <div>
              {getCell(
                mobileData.personGuid,
                mobileData.allPositions,
                CelKey.SUBUNIT_CREATE,
                mobileData.isExpired,
              )}
            </div>
          </div>,
          <div key="patrolEdit">
            <S bold size="5">
              <FormattedMessage id="permissionManager.tabTitle.patrolEdit" />
            </S>
            <div>
              {getCell(
                mobileData.personGuid,
                mobileData.allPositions,
                CelKey.SUBUNIT_EDIT,
                mobileData.isExpired,
              )}
            </div>
          </div>,
        ];
      }

      return [
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.patrolCreate',
          }),
          dataIndex: 'patrolCreate',
          key: 'patrolCreate',
          width: '12%',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.personGuid,
              data.allPositions,
              CelKey.SUBUNIT_CREATE,
              data.isExpired,
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'permissionManager.tabTitle.patrolEdit',
          }),
          dataIndex: 'patrolEdit',
          key: 'patrolEdit',
          width: '12%',
          render(_: unknown, data: RosterAdultMemberExtended) {
            return getCell(
              data.personGuid,
              data.allPositions,
              CelKey.SUBUNIT_EDIT,
              data.isExpired,
            );
          },
        },
      ];
    }

    return [];
  };

  const getProgramSpecificAdvancementColumns = (
    isAdult: boolean,
    mobileData?: RosterAdultMemberExtended | RosterYouthMemberExtended,
  ) => {
    const isMobileComponent = isMobile && mobileData;
    if (unitType === unitTypes.TROOP) {
      if (isMobileComponent) {
        return isAdult
          ? [
              <div key="mbList">
                <S bold size="5">
                  <FormattedMessage id="permissionManager.tabTitle.meritBadgeList" />
                </S>
                <div>
                  {getCell(
                    mobileData.personGuid,
                    mobileData.allPositions,
                    CelKey.ADVANCEMENT_MBLIST,
                    mobileData.isExpired,
                  )}
                </div>
              </div>,
            ]
          : [
              <div key="scoutEditor">
                <S bold size="5">
                  <FormattedMessage id="permissionManager.tabTitle.scoutEditor" />
                </S>
                <div>
                  {getCell(
                    mobileData.personGuid,
                    mobileData.allPositions,
                    CelKey.ADVANCEMENT_SCOUT_EDITOR,
                    mobileData.isExpired,
                  )}
                </div>
              </div>,
              <div key="tenderfootEditor">
                <S bold size="5">
                  <FormattedMessage id="permissionManager.tabTitle.tenderfootEditor" />
                </S>
                <div>
                  {getCell(
                    mobileData.personGuid,
                    mobileData.allPositions,
                    CelKey.ADVANCEMENT_TENDERFOOT_EDITOR,
                    mobileData.isExpired,
                  )}
                </div>
              </div>,
              <div key="secondClassEditor">
                <S bold size="5">
                  <FormattedMessage id="permissionManager.tabTitle.secondClassEditor" />
                </S>
                <div>
                  {getCell(
                    mobileData.personGuid,
                    mobileData.allPositions,
                    CelKey.ADVANCEMENT_SECONDCLASS_EDITOR,
                    mobileData.isExpired,
                  )}
                </div>
              </div>,
              <div key="firstClassEditor">
                <S bold size="5">
                  <FormattedMessage id="permissionManager.tabTitle.firstClassEditor" />
                </S>
                <div>
                  {getCell(
                    mobileData.personGuid,
                    mobileData.allPositions,
                    CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR,
                    mobileData.isExpired,
                  )}
                </div>
              </div>,
            ];
      }

      return isAdult
        ? [
            {
              title: (
                <Tooltip
                  title={intl.formatMessage({
                    id: 'permissionManager.tabTitle.mbcTooltip',
                  })}
                  trigger="hover"
                >
                  <div>
                    <FormattedMessage id="permissionManager.tabTitle.meritBadgeList" />
                  </div>
                </Tooltip>
              ),
              dataIndex: 'mbList',
              key: 'mbList',
              width: '12%',
              render(_: unknown, data: RosterAdultMemberExtended) {
                return getCell(
                  data.personGuid,
                  data.allPositions,
                  CelKey.ADVANCEMENT_MBLIST,
                  data.isExpired,
                );
              },
            },
          ]
        : [
            {
              title: intl.formatMessage({
                id: 'permissionManager.tabTitle.scoutEditor',
              }),
              dataIndex: 'scoutEditor',
              key: 'scoutEditor',
              width: '12%',
              render(_: unknown, data: RosterAdultMemberExtended) {
                return getCell(
                  data.personGuid,
                  data.allPositions,
                  CelKey.ADVANCEMENT_SCOUT_EDITOR,
                  data.isExpired,
                );
              },
            },
            {
              title: intl.formatMessage({
                id: 'permissionManager.tabTitle.tenderfootEditor',
              }),
              dataIndex: 'tenderfootEditor',
              key: 'tenderfootEditor',
              width: '12%',
              render(_: unknown, data: RosterAdultMemberExtended) {
                return getCell(
                  data.personGuid,
                  data.allPositions,
                  CelKey.ADVANCEMENT_TENDERFOOT_EDITOR,
                  data.isExpired,
                );
              },
            },
            {
              title: intl.formatMessage({
                id: 'permissionManager.tabTitle.secondClassEditor',
              }),
              dataIndex: 'secondClassEditor',
              key: 'secondClassEditor',
              width: '12%',
              render(_: unknown, data: RosterAdultMemberExtended) {
                return getCell(
                  data.personGuid,
                  data.allPositions,
                  CelKey.ADVANCEMENT_SECONDCLASS_EDITOR,
                  data.isExpired,
                );
              },
            },
            {
              title: intl.formatMessage({
                id: 'permissionManager.tabTitle.firstClassEditor',
              }),
              dataIndex: 'firstClassEditor',
              key: 'firstClassEditor',
              width: '12%',
              render(_: unknown, data: RosterAdultMemberExtended) {
                return getCell(
                  data.personGuid,
                  data.allPositions,
                  CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR,
                  data.isExpired,
                );
              },
            },
          ];
    }

    return [];
  };

  const getColumns = (
    type: PanelTitle,
    mobileData?: RosterAdultMemberExtended | RosterYouthMemberExtended,
  ) => {
    const isMobileComponent = isMobile && mobileData;
    switch (type) {
      case PanelTitle.ACTIVITY:
        if (isMobileComponent) {
          return [
            <div key="create">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.activityCreate" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.ACTIVITY_CREATE,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
          ];
        }

        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.activityCreate',
            }),
            dataIndex: 'create',
            key: 'create',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.ACTIVITY_CREATE,
                data.isExpired,
              );
            },
          },
        ];
      case PanelTitle.ADVANCEMENT:
        if (isMobileComponent) {
          return [
            <div key="award">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.advancementAward" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.ADVANCEMENT_AWARD,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            <div key="approve">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.advancementApprove" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.ADVANCEMENT_APROVE,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            <div key="edit">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.advancementEdit" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.ADVANCEMENT_EDIT,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            ...getProgramSpecificAdvancementColumns(true, mobileData),
          ];
        }

        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.advancementAward',
            }),
            dataIndex: 'award',
            key: 'award',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.ADVANCEMENT_AWARD,
                data.isExpired,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.advancementApprove',
            }),
            dataIndex: 'approve',
            key: 'approve',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.ADVANCEMENT_APROVE,
                data.isExpired,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.advancementEdit',
            }),
            dataIndex: 'edit',
            key: 'edit',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.ADVANCEMENT_EDIT,
                data.isExpired,
              );
            },
          },
          ...getProgramSpecificAdvancementColumns(true),
        ];
      case PanelTitle.ADVANCEMENT_YOUTH:
        if (isMobileComponent) {
          return [...getProgramSpecificAdvancementColumns(false, mobileData)];
        }

        return [...getProgramSpecificAdvancementColumns(false)];
      case PanelTitle.CALENDAR_YOUTH:
      case PanelTitle.CALENDAR:
        if (isMobileComponent) {
          return [
            <div key="create">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.calendarCreate" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.CALENDAR_CREATE,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
          ];
        }

        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.calendarCreate',
            }),
            dataIndex: 'create',
            key: 'create',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.CALENDAR_CREATE,
                data.isExpired,
              );
            },
          },
        ];
      case PanelTitle.FINANCE:
        if (isMobileComponent) {
          return [
            <div key="create">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.unitLogCreate" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.FINANCE_CREATE,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            <div key="indivCreate">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.inidvLogCreate" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.FINANCE_INDIVCREATE,
                )}
              </div>
            </div>,
            <div key="poCreate">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.poCreate" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.FINANCE_POCREATE,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
          ];
        }

        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.unitLogCreate',
            }),
            dataIndex: 'create',
            key: 'create',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.FINANCE_CREATE,
                data.isExpired,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.inidvLogCreate',
            }),
            dataIndex: 'indivCreate',
            key: 'indivCreate',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.FINANCE_INDIVCREATE,
                data.isExpired,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.poCreate',
            }),
            dataIndex: 'poCreate',
            key: 'poCreate',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.FINANCE_POCREATE,
                data.isExpired,
              );
            },
          },
        ];
      case PanelTitle.LEADERSHIP:
        if (isMobileComponent) {
          return [
            <div key="acceptance">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.leaderEdit" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.LEADERSHIP_EDIT,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            <div key="leaderApprove">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.leaderEditRestricted" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.LEADERSHIP_EDIT_RESTRICTED,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
          ];
        }

        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.leaderEdit',
            }),
            dataIndex: 'acceptance',
            key: 'acceptance',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.LEADERSHIP_EDIT,
                data.isExpired,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.leaderEditRestricted',
            }),
            dataIndex: 'leaderApprove',
            key: 'leaderApprove',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.LEADERSHIP_EDIT_RESTRICTED,
                data.isExpired,
              );
            },
          },
        ];
      case PanelTitle.MEMBERSHIP:
        if (isMobileComponent) {
          return [
            <div key="inactiveYouthMembers">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.inactiveYouthMembers" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.MEMBERSHIP_INACTIVE_YOUTH,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            <div key="inactiveAdultMembers">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.inactiveAdultMembers" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.MEMBERSHIP_INACTIVE_ADULT,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
          ];
        }

        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.inactiveYouthMembers',
            }),
            dataIndex: 'inactiveYouthMembers',
            key: 'inactiveYouthMembers',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.MEMBERSHIP_INACTIVE_YOUTH,
                data.isExpired,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.inactiveAdultMembers',
            }),
            dataIndex: 'inactiveAdultMembers',
            key: 'inactiveAdultMembers',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.MEMBERSHIP_INACTIVE_ADULT,
                data.isExpired,
              );
            },
          },
        ];
      case PanelTitle.MESSAGING_YOUTH:
      case PanelTitle.MESSAGING:
        if (isMobileComponent) {
          return [
            <div key="messageCreate">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.messageCreate" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.MESSAGING_CREATE,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            // TODO: Uncomment when group create exist
            // <div key="groupCreate">
            //   <S bold size="5">
            //     <FormattedMessage id="permissionManager.tabTitle.groupCreate" />
            //   </S>
            //   <div>
            //     {getCell(
            //       mobileData.personGuid,
            //       mobileData.allPositions,
            //       CelKey.MESSAGING_GROPU_CREATE,
            //       mobileData.isExpired,
            //     )}
            //   </div>
            // </div>,
          ];
        }

        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.messageCreate',
            }),
            dataIndex: 'messageCreate',
            key: 'messageCreate',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.MESSAGING_CREATE,
                data.isExpired,
              );
            },
          },
          // TODO: Uncomment when group create exist
          // {
          //   title: intl.formatMessage({
          //     id: 'permissionManager.tabTitle.groupCreate',
          //   }),
          //   dataIndex: 'groupCreate',
          //   key: 'groupCreate',
          //   width: '12%',
          //   render(_: unknown, data: RosterAdultMemberExtended) {
          //     return getCell(
          //       data.personGuid,
          //       data.allPositions,
          //       CelKey.MESSAGING_GROPU_CREATE,
          //       data.isExpired,
          //     );
          //   },
          // },
        ];
      case PanelTitle.UNIT:
        if (isMobileComponent) {
          return [
            <div key="profileEdit">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.profileEdit" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.UNIT_PROFILE_EDIT,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            <div key="unitEdit">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.unitEdit" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.UNIT_EDIT,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            <div key="reports">
              <S bold size="5">
                <FormattedMessage id="permissionManager.tabTitle.reports" />
              </S>
              <div>
                {getCell(
                  mobileData.personGuid,
                  mobileData.allPositions,
                  CelKey.UNIT_REPORTS,
                  mobileData.isExpired,
                )}
              </div>
            </div>,
            ...getProgramSpecificUnitColumns(mobileData),
          ];
        }

        return [
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.profileEdit',
            }),
            dataIndex: 'profileEdit',
            key: 'profileEdit',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.UNIT_PROFILE_EDIT,
                data.isExpired,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.unitEdit',
            }),
            dataIndex: 'unitEdit',
            key: 'unitEdit',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.UNIT_EDIT,
                data.isExpired,
              );
            },
          },
          {
            title: intl.formatMessage({
              id: 'permissionManager.tabTitle.reports',
            }),
            dataIndex: 'reports',
            key: 'reports',
            width: '12%',
            render(_: unknown, data: RosterAdultMemberExtended) {
              return getCell(
                data.personGuid,
                data.allPositions,
                CelKey.UNIT_REPORTS,
                data.isExpired,
              );
            },
          },
          ...getProgramSpecificUnitColumns(),
        ];
      default:
        return [];
    }
  };

  const columns = [
    {
      title: intl.formatMessage({
        id: 'shared.name',
      }),
      dataIndex: 'personFullName',
      key: 'personFullName',
      width: '20%',
      render(
        personFullName: string,
        {
          isAdult,
          pictureUrl,
          nickName,
          lastName,
          gender,
        }: RosterAdultMemberExtended | RosterYouthMemberExtended,
      ) {
        return (
          <div className={styles.nameContainer}>
            <PersonAvatar
              isAdult={isAdult}
              gender={gender}
              pictureUrl={pictureUrl}
              bordered={false}
              className={styles.avatar}
            />
            <S colored size="4">
              {nickName ? `${nickName} ${lastName}` : personFullName}
            </S>
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'permissionManager.tabTitle.role',
      }),
      dataIndex: 'allPositions',
      key: 'allPositions',
      width: '20%',
      render(allPositions: string[], item: RosterAdultMemberExtended) {
        if (item.isExpired) {
          return (
            <S size="5" className={styles.expiredTxt}>
              <FormattedMessage id="permissionManager.tabTitle.roleExpired" />
            </S>
          );
        }

        const filteredPositions = allPositions.filter(
          pos => !hidePositions.includes(pos),
        );

        return (
          <>
            {filteredPositions.map(pos => (
              <div key={`${item.memberId}-${pos}`}>
                <S size="5">
                  {pos === Positions.AAPR
                    ? Positions.AAPR.concat('/Editor/Recorder')
                    : pos}
                </S>
              </div>
            ))}
          </>
        );
      },
    },
    ...getColumns(panelType),
    {
      title: '',
      dataIndex: 'specifity',
      key: 'specifity',
    },
  ];

  const getPersonRowKey = (rowData: RosterAdultMemberExtended) => {
    const { userId } = rowData;
    return `${userId}`;
  };

  const renderTitle = () => (
    <div className={styles.tableTitle}>
      <Button
        type="ghost"
        noBorder
        size="small"
        icon={<MdAddCircle />}
        uppercase={false}
        className={styles.tableTitleBtn}
        onClick={handleSelectRecommended}
      >
        <FormattedMessage id="permissionManager.permissionTab.selectRecommended" />
      </Button>
      <Button
        type="ghost"
        noBorder
        size="small"
        icon={<MdOutlineRemoveCircle />}
        uppercase={false}
        className={styles.tableTitleBtn}
        onClick={hanldeRemoveRecommended}
      >
        <FormattedMessage id="permissionManager.permissionTab.removeRecommended" />
      </Button>
      <Input
        size="large"
        rounded
        addonAfter={<MdSearch />}
        className={styles.searchInput}
        placeholder="Search by Name"
        value={searchTxt}
        onChange={e => setSearchTxt(e.target.value)}
      />
      <PermsPanelFilterCard
        className={styles.positionSelect}
        globalPositionsFilter={globalPositionsFilter}
        onPositionFilterChange={handlePositionFilterChange}
      />
    </div>
  );

  if (isMobile) {
    return (
      <div className={styles.mobilePanelContainer}>
        <div className={styles.searchInputContainer}>
          <Input
            size="large"
            rounded
            addonAfter={<MdSearch />}
            className={styles.searchInput}
            placeholder="Search by Name"
            value={searchTxt}
            onChange={e => setSearchTxt(e.target.value)}
          />
          <PermsPanelFilterCard
            className={styles.positionSelect}
            onPositionFilterChange={handlePositionFilterChange}
          />
        </div>
        {filteredTableData.map(
          (
            tableData: RosterAdultMemberExtended | RosterYouthMemberExtended,
            index,
            ogArray,
          ) => {
            const columns = getColumns(
              panelType,
              tableData,
            ) as Array<React.JSX.Element>;
            return (
              <Fragment key={tableData.personGuid}>
                <div className={styles.userItemContainer}>
                  <div className={styles.userItemNameContainer}>
                    <PersonAvatar
                      isAdult={tableData.isAdult}
                      gender={tableData.gender}
                      pictureUrl={tableData.pictureUrl}
                      bordered={false}
                      className={styles.avatar}
                    />
                    <S colored size="4">
                      {tableData.personFullName}
                    </S>
                  </div>
                  <div className={styles.userItemPositionsContainer}>
                    {tableData.isExpired ? (
                      <S size="5" className={styles.expiredTxt}>
                        <FormattedMessage id="permissionManager.tabTitle.roleExpired" />
                      </S>
                    ) : (
                      <S size="5">
                        {tableData.userOrgPositions
                          .map(pos => pos.positionLong)
                          .toString()
                          .replaceAll(',', ', ')}
                      </S>
                    )}
                  </div>
                  {!tableData.isExpired && (
                    <div className={styles.userItemCkbxContainer}>
                      <ConditionalRender value={!isEmpty(columns)}>
                        {columns.map(colum => colum)}
                      </ConditionalRender>
                    </div>
                  )}
                </div>
                {index !== ogArray.length - 1 && <hr className={styles.hr} />}
              </Fragment>
            );
          },
        )}
      </div>
    );
  }

  return (
    <Table
      rowKey={getPersonRowKey}
      loading={loading}
      title={renderTitle}
      columns={columns}
      dataSource={filteredTableData}
      size="middle"
      scroll={{ y: 'calc(50vh - 80px)' }}
      pagination={false}
      className={styles.tableHeader}
    />
  );
};
